// src/pages/Workforce/AttendanceShared.jsx
import React from "react";
import { Check, AlertCircle, Star, X, PlaneTakeoff, CalendarDays, ShieldCheck, ShieldAlert, ShieldQuestion, MapPin } from "lucide-react";

export const STATUS_CFG = {
  present:  { icon: <Check size={16} className="text-green-500 stroke-[3]" />,        label: "Present"   },
  late:     { icon: <AlertCircle size={15} className="text-amber-500 fill-amber-100" />,label: "Late"      },
  half_day: { icon: <Star size={15} className="text-red-500 stroke-[2]" />,           label: "Half Day"  },
  absent:   { icon: <X size={15} className="text-gray-400 stroke-[3]" />,             label: "Absent"    },
  on_leave: { icon: <PlaneTakeoff size={15} className="text-red-600" />,              label: "On Leave"  },
  holiday:  { icon: <Star size={15} className="text-amber-400 fill-amber-400" />,     label: "Holiday"   },
  weekend:  { icon: <CalendarDays size={15} className="text-red-500" />,              label: "Day Off"   },
  future:   { icon: <span className="text-gray-300 text-xs font-bold">-</span>,       label: "—"         },
};

export function StatusCell({ day, onClick }) {
  if (!day) return (
    <td className="w-9 min-w-[36px] px-0.5 py-1.5 text-center align-middle">
      <span className="text-gray-300 text-xs font-bold">-</span>
    </td>
  );
  const cfg = STATUS_CFG[day.status] || STATUS_CFG.future;
  return (
    <td className="w-9 min-w-[36px] px-0.5 py-1.5 text-center align-middle">
      <button
        onClick={() => day.status !== "future" && day.status !== "weekend" && onClick && onClick(day)}
        title={cfg.label}
        className={`w-full h-full flex items-center justify-center transition ${day.status !== "future" && day.status !== "weekend" ? "cursor-pointer hover:scale-150" : "cursor-default"}`}
      >
        {cfg.icon}
      </button>
    </td>
  );
}

export const GeofenceBadge = ({ status }) => {
  if (!status) return null;
  const map = {
    verified:             { icon: <ShieldCheck   size={11} />, label: "On-Site",      cls: "bg-green-100 text-green-700 border-green-300"  },
    flagged_out_of_range: { icon: <ShieldAlert   size={11} />, label: "Out of Range", cls: "bg-red-100   text-red-700   border-red-300"    },
    geofence_unavailable: { icon: <ShieldQuestion size={11}/>, label: "No GPS Data",  cls: "bg-gray-100  text-gray-600  border-gray-300"   },
  };
  const cfg = map[status] || map.geofence_unavailable;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.cls}`}>
      {cfg.icon}{cfg.label}
    </span>
  );
};

export function DayModal({ cell, employeeName, onClose }) {
  if (!cell) return null;
  const cfg = STATUS_CFG[cell.status] || STATUS_CFG.future;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-[#161b22] rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10 border border-slate-200/80 dark:border-[#30363d]">
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 dark:text-slate-500 transition"><X size={18} /></button>
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-50 dark:bg-[#21262d] border border-gray-200 dark:border-[#30363d] text-sm font-bold mb-4`}>
          {cfg.icon} <span className="text-gray-700 dark:text-slate-200">{cfg.label}</span>
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-0.5">{employeeName}</h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Day {cell.day} — {cell.date_str || ""}</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-[#21262d] rounded-xl p-3 border border-gray-100 dark:border-[#30363d]">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1">Check In</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{cell.check_in || "—"}</p>
          </div>
          <div className="bg-gray-50 dark:bg-[#21262d] rounded-xl p-3 border border-gray-100 dark:border-[#30363d]">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1">Check Out</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{cell.check_out || "—"}</p>
          </div>
          <div className="bg-gray-50 dark:bg-[#21262d] rounded-xl p-3 border border-gray-100 dark:border-[#30363d]">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1">Hours Worked</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{cell.working_hours ? `${cell.working_hours}h` : "—"}</p>
          </div>
          <div className="bg-gray-50 dark:bg-[#21262d] rounded-xl p-3 border border-gray-100 dark:border-[#30363d]">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1">Geofence</p>
            <div className="mt-1"><GeofenceBadge status={cell.geofence_status} /></div>
          </div>
        </div>
        {cell.location && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400"><MapPin size={12} /> {cell.location}</div>
        )}
      </div>
    </div>
  );
}

export function Legend() {
  const items = [
    { ...STATUS_CFG.holiday,  label: "Holiday"  },
    { ...STATUS_CFG.weekend,  label: "Day Off"  },
    { ...STATUS_CFG.present,  label: "Present"  },
    { ...STATUS_CFG.half_day, label: "Half Day" },
    { ...STATUS_CFG.late,     label: "Late"     },
    { ...STATUS_CFG.absent,   label: "Absent"   },
    { ...STATUS_CFG.on_leave, label: "On Leave" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-[13px] text-gray-600 dark:text-slate-300 px-4 py-3 mb-4 shrink-0 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] shadow-xs rounded-lg">
      <span className="font-bold text-gray-800 dark:text-white tracking-wide mr-1">Note:</span>
      {items.map((it, idx) => (
        <React.Fragment key={it.label}>
          <span className="flex items-center gap-1.5">
            {it.icon}
            <span className="text-gray-400 dark:text-slate-500 mx-0.5">→</span>
            <span className="text-gray-600 dark:text-slate-300 font-medium">{it.label}</span>
          </span>
          {idx < items.length - 1 && <span className="text-gray-300 dark:text-slate-600 font-light px-1">|</span>}
        </React.Fragment>
      ))}
    </div>
  );
}